import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Modal, Timeline, Empty, Avatar, Space } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as workflowService from '../../services/workflowService';

const WorkflowDone: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<workflowService.WorkflowTask[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [currentHistory, setCurrentHistory] = useState<workflowService.WorkflowHistory[]>([]);

  useEffect(() => {
    loadData();
  }, [page]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await workflowService.getMyDoneTasks({ page, pageSize: 10 });
      if (res.data) {
        setTasks(res.data.list || []);
        setTotal(res.data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  const viewHistory = async (instanceId: number) => {
    try {
      const res = await workflowService.getInstanceHistory(instanceId);
      if (res.data) {
        setCurrentHistory(res.data as workflowService.WorkflowHistory[]);
        setHistoryModalVisible(true);
      }
    } catch {}
  };

  const columns: ColumnsType<workflowService.WorkflowTask> = [
    {
      title: '流程标题',
      dataIndex: ['instance', 'title'],
      key: 'title',
      render: (_, record) => record.instance?.title || '-',
    },
    { title: '处理节点', dataIndex: 'nodeName', key: 'nodeName' },
    {
      title: '处理结果',
      dataIndex: 'status',
      key: 'status',
      render: (status: number) => {
        const info = workflowService.taskStatusMap[status];
        return <Tag color={info?.color}>{info?.text}</Tag>;
      },
    },
    { title: '审批意见', dataIndex: 'comment', key: 'comment', ellipsis: true, width: 200 },
    {
      title: '处理时间',
      dataIndex: 'completeTime',
      key: 'completeTime',
      render: (time: string) => time?.substring(0, 16) || '-',
    },
    {
      title: '耗时',
      dataIndex: 'duration',
      key: 'duration',
      render: (duration: number) => {
        if (!duration) return '-';
        const hours = Math.floor(duration / 3600);
        const minutes = Math.floor((duration % 3600) / 60);
        return hours > 0 ? `${hours}小时${minutes}分钟` : `${minutes}分钟`;
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button size="small" icon={<HistoryOutlined />} onClick={() => viewHistory(record.instanceId)}>历史</Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="已办任务">
        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="id"
          loading={loading}
          pagination={{ current: page, total, pageSize: 10, showTotal: (t) => `共 ${t} 条`, onChange: setPage }}
        />
      </Card>

      <Modal title="审批历史" open={historyModalVisible} onCancel={() => setHistoryModalVisible(false)} footer={null} width={700}>
        {currentHistory.length > 0 ? (
          <Timeline
            items={currentHistory.map((h) => ({
              color: h.action === 'approve' ? 'green' : h.action === 'reject' ? 'red' : 'blue',
              children: (
                <div>
                  <Space><Avatar size="small">{h.operatorName?.charAt(0)}</Avatar><strong>{h.operatorName}</strong><Tag>{h.nodeName}</Tag></Space>
                  <div style={{ marginTop: 8, marginLeft: 32 }}>
                    <Tag color={h.action === 'approve' ? 'green' : h.action === 'reject' ? 'red' : 'blue'}>
                      {h.action === 'submit' && '发起流程'}{h.action === 'approve' && '审批通过'}{h.action === 'reject' && '审批拒绝'}
                    </Tag>
                    {h.comment && <div style={{ marginTop: 4, color: '#666', background: '#f5f5f5', padding: 8, borderRadius: 4 }}>{h.comment}</div>}
                  </div>
                </div>
              ),
            }))}
          />
        ) : <Empty description="暂无历史记录" />}
      </Modal>
    </div>
  );
};

export default WorkflowDone;
